import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  CasinoOutlined as RevolverIcon,
  SportsEsportsOutlined as MineIcon,
  AutoAwesomeOutlined as MRLIcon,
  RocketLaunchOutlined as MissileIcon,
  AppsOutlined as AllGamesIcon,
  CasinoOutlined as SlotIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx, customShadows, LOTTERY_NAMES, LOTTERY_TYPE_IDS, LOTTERY_COLOR_THEMES } from "../../../../../theme";
import {
  CommonInput,
  CommonSelect,
} from "../../../../Components/ui";
import { LOTTERY_SORT_OPTIONS } from "../../../../../static/Constants";

const GAME_TABS = [
  { id: "all", label: "All Games", icon: AllGamesIcon, color: colors.primary.main, bg: colors.blue[50] },
  { id: String(LOTTERY_TYPE_IDS.THREESLOT), label: "3-SlotBET", icon: SlotIcon, color: LOTTERY_COLOR_THEMES[LOTTERY_TYPE_IDS.THREESLOT].color, bg: LOTTERY_COLOR_THEMES[LOTTERY_TYPE_IDS.THREESLOT].bgColor },
  { id: String(LOTTERY_TYPE_IDS.FIVESLOT), label: "5-SlotBET", icon: SlotIcon, color: LOTTERY_COLOR_THEMES[LOTTERY_TYPE_IDS.FIVESLOT].color, bg: LOTTERY_COLOR_THEMES[LOTTERY_TYPE_IDS.FIVESLOT].bgColor },
  { id: String(LOTTERY_TYPE_IDS.MEGABET), label: LOTTERY_NAMES.MEGABET, icon: MRLIcon, color: LOTTERY_COLOR_THEMES[LOTTERY_TYPE_IDS.MEGABET].color, bg: LOTTERY_COLOR_THEMES[LOTTERY_TYPE_IDS.MEGABET].bgColor },
  { id: String(LOTTERY_TYPE_IDS.SPINBET), label: LOTTERY_NAMES.SPINBET, icon: RevolverIcon, color: LOTTERY_COLOR_THEMES[LOTTERY_TYPE_IDS.SPINBET].color, bg: LOTTERY_COLOR_THEMES[LOTTERY_TYPE_IDS.SPINBET].bgColor },
  { id: String(LOTTERY_TYPE_IDS.MINEBET), label: LOTTERY_NAMES.MINEBET, icon: MineIcon, color: LOTTERY_COLOR_THEMES[LOTTERY_TYPE_IDS.MINEBET].color, bg: LOTTERY_COLOR_THEMES[LOTTERY_TYPE_IDS.MINEBET].bgColor },
  { id: String(LOTTERY_TYPE_IDS.ROCKETBET), label: LOTTERY_NAMES.ROCKETBET, icon: MissileIcon, color: LOTTERY_COLOR_THEMES[LOTTERY_TYPE_IDS.ROCKETBET].color, bg: LOTTERY_COLOR_THEMES[LOTTERY_TYPE_IDS.ROCKETBET].bgColor },
];

const AllLotteriesFilters = ({
  activeTab,
  onTabChange,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  countsByType = {},
  onRefresh,
  refreshing,
}) => {
  return (
    <Card
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        mb: 3.5,
      }}
    >
      <Stack spacing={2.5}>
        {/* Top: Game Type Category Tabs */}
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{
            overflowX: "auto",
            pb: { xs: 1, sm: 0 },
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {GAME_TABS.map((tab) => {
            const isSelected = String(activeTab) === String(tab.id);
            const TabIcon = tab.icon;
            const count =
              tab.id === "all"
                ? countsByType.all || 0
                : countsByType[tab.id] || 0;

            return (
              <Box
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: borderRadiusPx.lg,
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease-in-out",
                  backgroundColor: isSelected ? colors.primary.main : colors.slate[50],
                  border: `1px solid ${isSelected ? colors.primary.main : colors.slate[200]}`,
                  boxShadow: isSelected ? "0 4px 12px rgba(37, 99, 235, 0.25)" : "none",
                  "&:hover": {
                    backgroundColor: isSelected ? colors.primary.dark : colors.slate[100],
                    borderColor: isSelected ? colors.primary.dark : colors.slate[300],
                  },
                }}
              >
                <TabIcon sx={{ fontSize: 18, color: isSelected ? "#ffffff" : tab.color }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 700 : 600,
                    fontSize: "0.875rem",
                    color: isSelected ? "#ffffff !important" : `${colors.slate[700]} !important`,
                  }}
                >
                  {tab.label}
                </Typography>
                <Box
                  sx={{
                    px: 0.85,
                    py: 0.15,
                    borderRadius: borderRadiusPx.full,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    backgroundColor: isSelected ? "rgba(255, 255, 255, 0.25)" : colors.slate[200],
                    color: isSelected ? "#ffffff" : colors.slate[700],
                  }}
                >
                  {count}
                </Box>
              </Box>
            );
          })}
        </Stack>

        {/* Bottom Bar: Search Input on Left, Sort Dropdown + Refresh on Right */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          {/* Search Input */}
          <Box sx={{ width: { xs: "100%", sm: "360px" } }}>
            <CommonInput
              fullWidth
              size="small"
              placeholder="Search by ID, creator, type..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.slate[400], fontSize: 20 }} />
                </InputAdornment>
              }
              endAdornment={
                searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => onSearchChange("")} sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            />
          </Box>

          {/* Right Side: Sort Dropdown + Refresh Button */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent={{ xs: "space-between", sm: "flex-end" }}
          >
            {/* Sort Dropdown */}
            <Box sx={{ minWidth: "170px" }}>
              <CommonSelect
                size="small"
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                options={LOTTERY_SORT_OPTIONS}
              />
            </Box>

            {/* Refresh Button */}
            <Tooltip title="Refresh On-Chain Data">
              <IconButton
                onClick={onRefresh}
                disabled={refreshing}
                sx={{
                  bgcolor: colors.slate[50],
                  color: colors.slate[600],
                  border: `1px solid ${colors.slate[200]}`,
                  "&:hover": {
                    bgcolor: colors.slate[100],
                    color: colors.slate[900],
                  },
                }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: 18,
                    animation: refreshing ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};

AllLotteriesFilters.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  countsByType: PropTypes.object,
  onRefresh: PropTypes.func.isRequired,
  refreshing: PropTypes.bool,
};

export default AllLotteriesFilters;
